import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Play, Pause, SkipForward, SkipBack, X, Volume2, VolumeX } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* GuidedTourSession — Apple-style auto-pilot tour across the property map.
 *
 * Flow:
 *  1. Visitor clicks "Explore" → Header dispatches `atrium:start-guided-tour`
 *  2. This component picks the most interesting N areas (in-progress first,
 *     then high-priority, capped at 8 so the tour stays under ~3 minutes)
 *  3. For each stop: smooth-scroll the hotspot into view, glow the hotspot,
 *     pop a narration card, and play Heavenly's `nova` TTS speaking the
 *     area's description
 *  4. Auto-advances when audio ends; visitor can Pause / Next / Prev / Exit
 *
 * Designed to coexist with the rest of the app — does NOT mount a modal
 * blocker, just an overlay with controls + a narration card so the map
 * stays interactive.
 */

const MAX_STOPS = 8;
const FALLBACK_HOLD_MS = 6500;   // if TTS fails, hold this long before advance
const HIGHLIGHT_CLASS = 'guided-tour-highlight';
const NAR_CHAR_CAP = 480;        // keep TTS calls snappy (~12-18s clips)

function pickTourAreas(areas) {
  // Filter out hidden areas; sort to prioritise active reconstruction
  // work first (more interesting to a visitor than 100% complete areas).
  const visible = (areas || []).filter((a) => !a.hidden && a.position_x != null && a.position_y != null);
  const scored = visible
    .map((a) => {
      const status = (a.status || '').toLowerCase();
      const progress = Number(a.progress_percent || 0);
      // Lower score = earlier in tour.
      let score = 50;
      if (status === 'reconstruction' || status === 'in-progress') score -= 30;
      if (status === 'crucial') score -= 25;
      if (status === 'requested') score -= 10;
      if (progress > 0 && progress < 100) score -= 20;
      if (progress === 100) score += 30; // tour 100% complete LAST
      // Tiebreaker: areas with renders are more visually interesting.
      if ((a.renders || []).length > 0) score -= 5;
      return { a, score };
    })
    .sort((x, y) => x.score - y.score)
    .slice(0, MAX_STOPS)
    .map((s) => s.a);
  return scored;
}

function narrationFor(area, index, total) {
  const stop = `Stop ${index + 1} of ${total}.`;
  const name = (area.name || 'Unnamed area').replace(/_/g, ' ');
  const progress = area.progress_percent != null ? `${Math.round(area.progress_percent)} percent complete.` : '';
  const status = area.status ? `Status: ${area.status}.` : '';
  const desc = (area.description || '').trim();
  const enhancement = area.enhancements_roi ? ` Enhancement focus: ${area.enhancements_roi.slice(0, 140)}` : '';
  let text = `${stop} ${name}. ${progress} ${status} ${desc}${enhancement}`.trim();
  if (text.length > NAR_CHAR_CAP) {
    text = text.slice(0, NAR_CHAR_CAP - 1).replace(/\s+\S*$/, '') + '…';
  }
  return text.replace(/\s+/g, ' ');
}

export default function GuidedTourSession({ areas, onAreaOpen }) {
  const [open, setOpen] = useState(false);
  const [stops, setStops] = useState([]);
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(false);
  const [loadingAudio, setLoadingAudio] = useState(false);
  const audioRef = useRef(null);
  const advanceTimerRef = useRef(null);

  // ─── Tour lifecycle ────────────────────────────────────────────────
  useEffect(() => {
    const handler = () => {
      const picked = pickTourAreas(areas);
      if (picked.length === 0) return;
      setStops(picked);
      setIdx(0);
      setPlaying(true);
      setOpen(true);
    };
    window.addEventListener('atrium:start-guided-tour', handler);
    return () => window.removeEventListener('atrium:start-guided-tour', handler);
  }, [areas]);

  // ─── Scroll + highlight on stop change ─────────────────────────────
  const focusCurrentHotspot = useCallback(() => {
    const stop = stops[idx];
    if (!stop) return;
    // Remove any previous highlight
    document.querySelectorAll(`.${HIGHLIGHT_CLASS}`).forEach((el) => el.classList.remove(HIGHLIGHT_CLASS));
    // Smooth-scroll + glow on the new one.
    const el = document.querySelector(`[data-testid="hotspot-${stop.id}"]`);
    if (el) {
      el.classList.add(HIGHLIGHT_CLASS);
      el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
    }
  }, [stops, idx]);

  // ─── Audio playback ────────────────────────────────────────────────
  const playAudio = useCallback(async (text) => {
    if (muted) {
      // Fallback: hold the card for a fixed time even when muted.
      if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current);
      advanceTimerRef.current = setTimeout(() => {
        setIdx((i) => (i + 1 < stops.length ? i + 1 : i));
      }, FALLBACK_HOLD_MS);
      return;
    }
    try {
      setLoadingAudio(true);
      const { data } = await axios.post(`${API}/auth/tutorial-narration`, {
        text,
        voice: 'nova',
        lang: 'en',
      });
      const src = `data:${data.mime};base64,${data.audio_base64}`;
      if (audioRef.current) {
        try { audioRef.current.pause(); } catch (e) { /* ignore */ }
      }
      const a = new Audio(src);
      audioRef.current = a;
      a.onended = () => {
        if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current);
        advanceTimerRef.current = setTimeout(() => {
          setIdx((i) => (i + 1 < stops.length ? i + 1 : i));
        }, 700);
      };
      await a.play();
    } catch (e) {
      // TTS down or autoplay blocked — fall back to a fixed hold.
      if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current);
      advanceTimerRef.current = setTimeout(() => {
        setIdx((i) => (i + 1 < stops.length ? i + 1 : i));
      }, FALLBACK_HOLD_MS);
    } finally {
      setLoadingAudio(false);
    }
  }, [muted, stops.length]);

  // Drive every stop change.
  useEffect(() => {
    if (!open || stops.length === 0) return;
    focusCurrentHotspot();
    if (playing) {
      const stop = stops[idx];
      const text = narrationFor(stop, idx, stops.length);
      playAudio(text);
    }
    return () => {
      if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current);
      if (audioRef.current) {
        try { audioRef.current.pause(); } catch (e) { /* ignore */ }
      }
    };
    // playAudio + focusCurrentHotspot are stable per stops/idx/muted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, idx, stops.length, playing, muted]);

  // ─── Cleanup on close ──────────────────────────────────────────────
  const closeTour = useCallback(() => {
    setOpen(false);
    setPlaying(false);
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch (e) { /* ignore */ }
      audioRef.current = null;
    }
    if (advanceTimerRef.current) clearTimeout(advanceTimerRef.current);
    document.querySelectorAll(`.${HIGHLIGHT_CLASS}`).forEach((el) => el.classList.remove(HIGHLIGHT_CLASS));
  }, []);

  // Auto-close after the final stop's audio ends.
  useEffect(() => {
    if (!open || stops.length === 0) return;
    if (idx >= stops.length) {
      const t = setTimeout(closeTour, 1500);
      return () => clearTimeout(t);
    }
  }, [open, idx, stops.length, closeTour]);

  // Controls
  const handleNext = () => setIdx((i) => Math.min(stops.length - 1, i + 1));
  const handlePrev = () => setIdx((i) => Math.max(0, i - 1));
  const handlePauseToggle = () => {
    setPlaying((p) => {
      const next = !p;
      if (!next && audioRef.current) {
        try { audioRef.current.pause(); } catch (e) { /* ignore */ }
      } else if (next && audioRef.current) {
        try { audioRef.current.play(); } catch (e) { /* ignore */ }
      }
      return next;
    });
  };
  const handleMuteToggle = () => setMuted((m) => !m);
  const handleOpenArea = () => {
    const stop = stops[idx];
    if (!stop || !onAreaOpen) return;
    // Pause tour so the area dialog can be inspected without audio competing.
    setPlaying(false);
    if (audioRef.current) {
      try { audioRef.current.pause(); } catch (e) { /* ignore */ }
    }
    onAreaOpen(stop);
  };

  if (!open || stops.length === 0) return null;
  const stop = stops[idx];
  const narration = narrationFor(stop, idx, stops.length);

  return (
    <>
      {/* Floating narration card — bottom-left, doesn't block the map. */}
      <div
        data-testid="guided-tour-card"
        className="fixed bottom-6 left-6 right-6 sm:right-auto sm:max-w-sm z-[90] rounded-2xl overflow-hidden shadow-2xl"
        style={{ backgroundColor: 'white', border: '1px solid #E2DBC8', fontFamily: 'Manrope' }}
      >
        <div className="px-5 py-3 flex items-center justify-between gap-3" style={{ background: 'linear-gradient(135deg, #003DA5 0%, #1F4FBE 100%)' }}>
          <div className="flex items-center gap-2 min-w-0">
            <span className="inline-flex w-7 h-7 items-center justify-center rounded-full text-white font-bold" style={{ backgroundColor: 'rgba(255,255,255,0.18)', fontSize: 11 }}>
              {idx + 1}
            </span>
            <div className="min-w-0">
              <p className="text-[9px] uppercase tracking-[0.2em] font-bold text-white/80">Heavenly · Guided Tour</p>
              <p className="text-sm font-bold text-white truncate">{stop.name}</p>
            </div>
          </div>
          <button data-testid="guided-tour-close" onClick={closeTour} className="p-1.5 rounded-full text-white hover:bg-white/10">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4">
          <p className="text-sm leading-relaxed" style={{ color: '#1A2E2E' }}>
            {narration}
          </p>
          {/* Progress bar */}
          <div className="mt-3 mb-3 h-1.5 w-full rounded-full overflow-hidden" style={{ backgroundColor: '#EAE5D9' }}>
            <div
              className="h-full transition-all"
              style={{ width: `${((idx + 1) / stops.length) * 100}%`, backgroundColor: '#003DA5' }}
            />
          </div>
          <div className="flex items-center justify-between">
            <button
              data-testid="guided-tour-open-area"
              onClick={handleOpenArea}
              className="text-[11px] font-bold underline"
              style={{ color: '#1F4FBE' }}
              title="Open this area's detail dialog"
            >
              Open details →
            </button>
            <div className="flex items-center gap-1">
              <button data-testid="guided-tour-mute" onClick={handleMuteToggle} className="p-1.5 rounded-lg hover:bg-slate-100" title={muted ? 'Unmute Heavenly' : 'Mute Heavenly'}>
                {muted ? <VolumeX className="w-4 h-4" style={{ color: '#94A3B8' }} /> : <Volume2 className="w-4 h-4" style={{ color: '#1F4FBE' }} />}
              </button>
              <button data-testid="guided-tour-prev" onClick={handlePrev} disabled={idx === 0} className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-40">
                <SkipBack className="w-4 h-4" style={{ color: '#1F4FBE' }} />
              </button>
              <button data-testid="guided-tour-pause" onClick={handlePauseToggle} className="p-1.5 rounded-lg hover:bg-slate-100" title={playing ? 'Pause' : 'Resume'}>
                {playing ? <Pause className="w-4 h-4" style={{ color: '#1F4FBE' }} /> : <Play className="w-4 h-4" style={{ color: '#1F4FBE' }} />}
              </button>
              <button data-testid="guided-tour-next" onClick={handleNext} disabled={idx >= stops.length - 1} className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-40">
                <SkipForward className="w-4 h-4" style={{ color: '#1F4FBE' }} />
              </button>
            </div>
          </div>
          {loadingAudio && (
            <p className="mt-2 text-[10px] italic" style={{ color: '#7A8C8C' }}>Heavenly is speaking…</p>
          )}
        </div>
      </div>

      {/* Highlight CSS injected once so the hotspot glows. */}
      <style>{`
        .${HIGHLIGHT_CLASS} {
          animation: guided-tour-pulse 1.4s ease-in-out infinite;
          z-index: 50 !important;
        }
        .${HIGHLIGHT_CLASS} > span:first-child {
          background-color: rgba(0, 61, 165, 0.4) !important;
        }
        @keyframes guided-tour-pulse {
          0%   { transform: translate(-50%, -50%) scale(1);   filter: drop-shadow(0 0 0 rgba(0,61,165,0.0)); }
          50%  { transform: translate(-50%, -50%) scale(1.25); filter: drop-shadow(0 0 14px rgba(0,61,165,0.55)); }
          100% { transform: translate(-50%, -50%) scale(1);   filter: drop-shadow(0 0 0 rgba(0,61,165,0.0)); }
        }
      `}</style>
    </>
  );
}
